import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrashAlt } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from "../../utils/alertHelper";

export default function CategoryOffer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userCategories, setUserCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    categoryId: '',
    startDate: '',
    endDate: '',
    rows: [
      { itemId: '', originalPrice: '', finalPrice: '' }
    ]
  });

  useEffect(() => {
    async function fetchLists() {
      try {
        // attempt to fetch current user (optional)
        try { await axios.get('/api/user'); } catch (e) { }

        const [cRes, iRes] = await Promise.all([
          axios.get('/admin/api/userCategory'),
          axios.get('/admin/api/item')
        ]);

        const normalizeItems = (res) => {
          const d = res?.data ?? res;
          if (Array.isArray(d)) return d;
          if (Array.isArray(d.Items)) return d.Items;
          if (Array.isArray(d.items)) return d.items;
          if (Array.isArray(d.data)) return d.data;
          if (Array.isArray(res?.data?.Items)) return res.data.Items;
          if (Array.isArray(res?.Items)) return res.Items;
          if (d && typeof d === 'object') return Object.values(d);
          return [];
        };

        const cats = cRes.data?.userCategories || cRes.data?.data || cRes.data || [];
        const itemList = normalizeItems(iRes);
        // Only expose items that are available
        const availableItems = Array.isArray(itemList)
          ? itemList.filter((it) => it && (it.isAvailable === true || it.isAvailable === 'true'))
          : [];

        setUserCategories(cats);
        setItems(availableItems);

        // if editing, fetch existing category offer and populate form
        if (id) {
          try {
            const tryUrls = [`/admin/api/priceCategory/${id}`];
            let res;
            for (const u of tryUrls) {
              try { res = await axios.get(u); if (res) break; } catch (e) { }
            }
            if (res) {
              const d = res.data ?? res;
              const payload = d.priceCategories || d.priceCategory || d.data || d;
              const offer = Array.isArray(payload) ? payload[0] : payload;
              const normalizePriceFromItem = (it) => {
                if (!it) return '';
                if (it.salesPrice) return it.salesPrice;
                if (Array.isArray(it.stateRate) && it.stateRate[0]?.price) return it.stateRate[0].price;
                return it.price || it.rate || it.originalPrice || '';
              };
              const rows = (Array.isArray(offer.items) ? offer.items : []).map(it => {
                const itemId = it?.item?._id || it?.item || it.itemId || it._id || it.id || '';
                const offerPrice = it?.offerPrice ?? it?.finalPrice ?? '';
                const sel = itemList.find(itm => itm._id === itemId || itm.item?._id === itemId || itm.id === itemId);
                const originalPrice = sel ? normalizePriceFromItem(sel.item || sel) : (it.originalPrice || it.item?.price || it.price || '');
                return { itemId, originalPrice, finalPrice: offerPrice };
              });
              setForm(prev => ({
                ...prev,
                categoryId: offer.userCategory?._id || offer.userCategory || offer.userCategoryId || offer.category?._id || offer.category || '',
                startDate: offer.startDate ? String(offer.startDate).split('T')[0] : (offer.startDate || ''),
                endDate: offer.endDate ? String(offer.endDate).split('T')[0] : (offer.endDate || ''),
                rows: rows.length ? rows : prev.rows,
              }));
            }
          } catch (e) {
            console.error('Failed to load category offer for edit', e);
          }
        }
      } catch (err) {
        console.error(err);
        toastError('Failed to load user categories or items');
      }
    }
    fetchLists();
  }, []);

  const handleRowChange = (idx, field, value) => {
    setForm(prev => {
      const rows = [...prev.rows];
      rows[idx] = { ...rows[idx], [field]: value };
      // If itemId changed, try to fill originalPrice from items list
      if (field === 'itemId') {
        const sel = items.find(it => it._id === value || it.item?._id === value);
        const normalizePriceFromItem = (it) => {
          if (!it) return '';
          if (it.salesPrice) return it.salesPrice;
          if (Array.isArray(it.stateRate) && it.stateRate[0]?.price) return it.stateRate[0].price;
          return it.price || it.rate || '';
        };
        rows[idx].originalPrice = sel ? normalizePriceFromItem(sel.item || sel) : '';
      }
      return { ...prev, rows };
    });
  };

  const addRow = () => {
    setForm(prev => ({ ...prev, rows: [...prev.rows, { itemId: '', originalPrice: '', finalPrice: '' }] }));
  };

  const removeRow = (idx) => {
    setForm(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        userCategory: form.categoryId,
        startDate: form.startDate,
        endDate: form.endDate,
        items: form.rows.map(r => ({ item: r.itemId, finalPrice: Number(r.finalPrice) }))
      };
      if (id) {
        await axios.put(`/admin/api/priceCategory/${id}`, payload);
      } else {
        await axios.post('/admin/api/priceCategory', payload);
      }
      toastSuccess('Offer saved');
      navigate('/admin/price');
    } catch (err) {
      console.error(err);
      toastError('Failed to save offer');
    }
  };

  return (
    <div className="container-fluid py-4 px-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">Category Offer</h1>
        <a href="/admin/price" className="text-decoration-none text-secondary">← Back</a>
      </div>

      <form className="rounded-3 p-4" style={{ background: '#F7F7F8', border: '1.5px solid #BBBFD2' }} onSubmit={handleSubmit}>
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <label className="form-label">Category</label>
            <select className="form-select" value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })} required>
              <option value="">Select Category</option>
              {userCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
          </div>
          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
          </div>
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Sr.no</th>
              <th>Item</th>
              <th>Original Price</th>
              <th>Final Price</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {form.rows.map((r, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <select className="form-select" value={r.itemId} onChange={e => handleRowChange(idx, 'itemId', e.target.value)} required>
                    <option value="">Select Item</option>
                    {items.map((it, idx) => {
                      const id = it._id || it.item?._id || it.id || idx;
                      const name = it.name || it.item?.name || it.Item?.name || it.title || it.skucode || it.code || '';
                      return <option key={id} value={id}>{name}</option>;
                    })}
                  </select>
                </td>
                <td>
                  <input type="text" className="form-control" value={r.originalPrice} readOnly />
                </td>
                <td>
                  <input type="number" className="form-control" value={r.finalPrice} onChange={e => handleRowChange(idx, 'finalPrice', e.target.value)} required />
                </td>
                <td>
                  <div className="d-flex">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm p-1 d-flex align-items-center justify-content-center"
                      onClick={() => removeRow(idx)}
                      title="Remove"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mb-3">
          <button type="button" className="btn btn-primary" onClick={addRow}>+ </button>
        </div>

        <div className="d-flex justify-content-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/price')}>Cancel</button>
          <button type="submit" className="btn btn-primary">Save Offer</button>
        </div>
      </form>
    </div>
  );
}